import Modal from "@/common/components/Modal"
import { zodResolver } from "@hookform/resolvers/zod";
import { BadgePlus } from "lucide-react"
import { PrivateClientsFunnelValidationSchema, createSchema } from "../schemas/create.schema";
import TextField from "@/common/components/TextField";
import TextArea from "@/common/components/TextArea";
import { useQueryClient } from "@tanstack/react-query";
import MaskedTextField from "@/common/components/MaskedTextField";
import { cnpjCpfMask, phoneMask } from "@/common/constants/masks.constant";
import { useState } from "react";
import { options as monthDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/monthDaysOptions.constant'
import { options as weekDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/weekDaysOptions.constants'
import { options as cycleOptions } from '@/features/privateCompanyLeadsFunnel/constants/cycleOptions.constant'
import useCreatePrivateCompanyLead from "../hooks/useCreatePrivateCompanyLead";
import { FormTransformers } from "@/common/utils/formTransformers";
import Button from "../../../common/components/Button";
import { DirtySubmitHandler, useCustomForm } from "@/common/hooks/useCustomForm";
import SelectController from "@/common/components/SelectController";

const CreateModal = () => {
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false);
  const [serverError, setServerError] = useState(false);

  const {
    register,
    control,
    setValue,
    watch,
    handleDirtySubmit,
    reset: resetForm,
    formState: { errors },
    setError
  } = useCustomForm<PrivateClientsFunnelValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const onSubmit: DirtySubmitHandler<PrivateClientsFunnelValidationSchema> = (data) => createPrivateCompanyLead(data);

  const onSuccess = () => {
    resetForm()
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeads'] })
    setIsOpen(false)
  }

  const watchCycle = watch('contract.cycle')

  const {
    mutate: createPrivateCompanyLead,
    isPending: isCreatePrivateCompanyLeadPending,
  } = useCreatePrivateCompanyLead(onSuccess,
    FormTransformers.errorsTransformer<PrivateClientsFunnelValidationSchema>({ setError }));
    
  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleDirtySubmit(onSubmit)}
      Trigger={<Button name="open_create_lead_modal" className="bg-transparent hover:bg-transparent p-0"
        operationKlassToCheckPermission={"Admin::Funnel::PrivateCompanyLeads::Create"}><BadgePlus color="#7CC4F8" strokeWidth={1.5} /></Button>}
      title='Cadastrando oportunidade'
      dataTestid="create_private_company_funnel"
      footer="Cadastrar"
      disabled={isCreatePrivateCompanyLeadPending}
      confirmButtonIsLoading={isCreatePrivateCompanyLeadPending}
      body={(
        <form className="flex flex-col gap-4">
          {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>}
          <div className="flex gap-4">
            <TextField
              className="w-full"
              label="Nome da empresa"
              markAsRequired
              errorMessage={errors?.display_name?.message || undefined}
              data-testid="display_name_input"
              {...register('display_name')}
            />
            <MaskedTextField
              className="w-full"
              label="CNPJ/CPF"
              markAsRequired
              mask={cnpjCpfMask}
              errorMessage={errors?.general_information?.cnpj?.message || undefined}
              {...register('general_information.cnpj')}
            />
          </div>
          <div className="flex gap-4">
            <TextField
              className="w-full"
              label="Nome do Contato"
              markAsRequired
              errorMessage={errors?.contacts?.full_name?.message}
              data-testid="full_name_input"
              {...register('contacts.full_name')}
            />
            <TextField
              className="w-full"
              label="Cargo"
              maxLength={32}
              errorMessage={errors?.contacts?.role_in_company?.message}
              data-testid="role_in_company_input"
              {...register('contacts.role_in_company')}
            />
          </div>
          <div className="flex gap-4">
            <MaskedTextField
              className="w-full"
              label="Telefone do Contato"
              markAsRequired
              mask={phoneMask}
              errorMessage={errors?.contacts?.phone?.number?.message}
              {...register('contacts.phone.number')}
            />
            <TextField
              className="w-full"
              label="Email de Contato"
              markAsRequired
              errorMessage={errors?.contacts?.email?.message}
              data-testid="email_input"
              {...register('contacts.email')}
            />
          </div>
          <div className="flex gap-4">
            <SelectController
              control={control}
              name="contract.cycle"
              label="Ciclo de fatura"
              options={cycleOptions}
              className="flex-1"
              triggerOnChange={({ value }) => {
                setValue('contract.cycle_start_day', value === 'weekly' ? weekDaysOptions[0] : monthDaysOptions[0])
              }}
              defaultValue={cycleOptions.find((option) => option.value === 'monthly')}
              isDisabled={isCreatePrivateCompanyLeadPending}
              errorMessage={errors?.contract?.cycle?.message}
            />
            {watchCycle?.value === 'fixed' && (
              <MaskedTextField
                label="Intervalo de ciclo fixo"
                placeholder="Quantidade em dias"
                mask='0000'
                className="flex-1"
                {...register('contract.cycle_fixed_interval')}
                errorMessage={errors?.contract?.cycle_fixed_interval?.message || undefined}
              />
            )}
            {watchCycle?.value !== 'fixed' && (
              <SelectController
                control={control}
                name="contract.cycle_start_day"
                label="Dia de abertura"
                className="flex-1"
                defaultValue={{label: '1', value: '1'}}
                options={watchCycle?.value === 'weekly' ? weekDaysOptions : monthDaysOptions(28)}
                isDisabled={isCreatePrivateCompanyLeadPending}
                errorMessage={errors?.contract?.cycle_start_day?.message}
              />
            )}
          </div>
          <TextArea
            label="Suas observações"
            markAsRequired
            maxLength={1500}
            errorMessage={errors?.observations?.message}
            data-testid="observations_input"
            {...register('observations')}
          />
        </form>
      )}
    />
  )
}

export default CreateModal
